import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React from "react";
import { Glossary } from "src/components/Text/Glossary";
import { useGlossary } from "src/hooks/useGlossary";

export type OverrideReasonConfig = {
  reasonType: "freeText" | "options";
  reasonRequired: boolean;
  reasonOptions: string[];
};

type Props = {
  isOpen: boolean;
  config: OverrideReasonConfig;
  onCancel: () => void;
  onConfirm: (reason: string | null) => void;
};

export const OverrideReasonModal: React.FC<Props> = ({
  isOpen,
  config,
  onCancel,
  onConfirm,
}) => {
  const { glossary } = useGlossary();
  const [reason, setReason] = React.useState("");

  // Reset the captured reason each time the modal opens.
  React.useEffect(() => {
    if (isOpen) {
      setReason("");
    }
  }, [isOpen]);

  const trimmed = reason.trim();
  const confirmDisabled = config.reasonRequired && trimmed === "";

  // De-duplicate so repeated reasons don't produce colliding radio keys.
  const options = Array.from(
    new Set(
      config.reasonOptions
        .map((option) => option.trim())
        .filter((option) => option !== "")
    )
  );

  return (
    <Modal isOpen={isOpen} onClose={onCancel} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Glossary>Select ineligible school?</Glossary>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={4}>
            <Text>
              <Glossary>
                Selecting a school the student is not eligible for may have
                cascading, unintended effects that may also be visible to the
                family.
              </Glossary>
            </Text>
            <FormControl isRequired={config.reasonRequired}>
              <FormLabel>
                <Glossary>Reason for eligibility override</Glossary>
              </FormLabel>
              {config.reasonType === "options" ? (
                <RadioGroup value={reason} onChange={setReason}>
                  <Flex direction="column" gap={2}>
                    {options.map((option) => (
                      <Radio key={option} value={option}>
                        {option}
                      </Radio>
                    ))}
                  </Flex>
                </RadioGroup>
              ) : (
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={glossary`Enter a reason for selecting a school the student is not eligible for`}
                />
              )}
            </FormControl>
            <Text fontSize="sm" color="gray.500">
              <Glossary>This reason is for internal use only</Glossary>
            </Text>
          </Flex>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" colorScheme="gray" onClick={onCancel}>
            <Glossary>Cancel</Glossary>
          </Button>
          <Button
            isDisabled={confirmDisabled}
            onClick={() => onConfirm(trimmed === "" ? null : trimmed)}
          >
            <Glossary>Yes, select ineligible school</Glossary>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
